import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Leaf } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PasswordInput } from "../components/PasswordInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const sendForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
    } catch {
      toast.error("İşlem başarısız, tekrar deneyin");
    } finally {
      setForgotLoading(false);
    }
  };

  if (user && user !== false) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div
        className="relative hidden lg:flex lg:w-1/2"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/28867161/pexels-photo-28867161.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1000)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center bg-[#1C4E35]">
              <Leaf size={20} className="text-[#8FD4AC]" />
            </div>
            <span className="font-heading text-lg font-black tracking-tight text-white">COP31 PORTAL</span>
          </div>
          <div>
            <h2 className="font-heading text-4xl font-black leading-tight tracking-tight text-white">
              Rate Card
              <br />
              Sipariş Takip Sistemi
            </h2>
            <p className="mt-4 max-w-md text-sm text-stone-300">
              COP31 katalog ve sipariş verilerine tek noktadan erişin. Ürüne ve sipariş verene göre filtreleyin,
              firmanıza atanan siparişleri anlık takip edin.
            </p>
            <p className="mt-4 max-w-md border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-xs font-medium text-amber-200" data-testid="login-admin-only-notice">
              Bu sayfa katılımcılar için değildir; yalnızca sistem yöneticilerinin kullanımına yöneliktir.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center bg-[#F7F7F6] px-6">
        <div className="w-full max-w-sm fade-up">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <div className="flex h-9 w-9 items-center justify-center bg-[#1C4E35]">
              <Leaf size={18} className="text-[#8FD4AC]" />
            </div>
            <span className="font-heading text-base font-black tracking-tight">COP31 PORTAL</span>
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-stone-500">Güvenli Giriş</p>
          <h1 className="font-heading mt-2 text-3xl font-black tracking-tight text-[#0A0D0B]">Oturum Aç</h1>
          <p className="mt-2 text-sm text-stone-500">Portala erişmek için hesap bilgilerinizi girin.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-stone-600">
                E-posta
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ornek@firma.com"
                data-testid="login-email-input"
                className="rounded-sm border-stone-300 bg-white"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs uppercase tracking-wider text-stone-600">
                  Şifre
                </Label>
                <button type="button" onClick={() => { setForgotOpen(true); setForgotSent(false); setForgotEmail(email); }} data-testid="forgot-password-link" className="text-xs text-[#1C4E35] hover:underline">
                  Şifremi unuttum
                </button>
              </div>
              <PasswordInput id="password" value={password} onChange={(e) => setPassword(e.target.value)} testId="login-password-input" />
            </div>
            {error && (
              <p className="border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="login-error">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full rounded-sm bg-[#1C4E35] text-white transition-colors duration-200 hover:bg-[#143A26] active:scale-95"
            >
              {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-stone-500">
            Hesabınız yok mu?{" "}
            <Link to="/kayit" className="font-medium text-[#1C4E35] hover:underline" data-testid="go-to-register-link">
              Hesap oluşturun
            </Link>
            <span className="mt-1 block text-[11px] text-stone-400">Yeni hesaplar yönetici onayından sonra aktif olur.</span>
          </p>

          <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
            <DialogContent aria-describedby={undefined} className="rounded-sm bg-white sm:max-w-sm" data-testid="forgot-password-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-base font-bold tracking-tight">Şifremi Unuttum</DialogTitle>
              </DialogHeader>
              {forgotSent ? (
                <p className="text-sm text-stone-600" data-testid="forgot-success-message">
                  Eğer bu e-posta kayıtlıysa, şifre sıfırlama bağlantısı gönderildi. Gelen kutunuzu (ve spam klasörünü) kontrol edin. Bağlantı 1 saat geçerlidir.
                </p>
              ) : (
                <form onSubmit={sendForgot} className="space-y-4">
                  <p className="text-sm text-stone-600">E-posta adresinizi girin; size şifre sıfırlama bağlantısı gönderelim.</p>
                  <Input type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="ornek@firma.com" data-testid="forgot-email-input" className="rounded-sm border-stone-300" />
                  <Button type="submit" disabled={forgotLoading} data-testid="forgot-submit-button" className="w-full rounded-sm bg-[#1C4E35] text-white hover:bg-[#143A26]">
                    {forgotLoading ? "Gönderiliyor..." : "Sıfırlama Bağlantısı Gönder"}
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
